import { useState } from 'react';
import { Check, Plus, Sparkles, X } from 'lucide-react';

import type { ApplicabilitySuggestion, SopApplicabilityInput, SopTargetType } from '@/api/sops.api';
import type { Department } from '@/api/employees.api';
import type { SeatView } from '@/api/seats.api';
import { Button } from '@/components/ui/Button';
import { SYSTEM_ROLES, type SystemRole } from '@/types';

/**
 * Shared applicability picker (ADR-037) — reused by the mass-upload review
 * rows and the SOP detail modal. Mirrors the documents `GrantsEditor` but
 * drops the per-person (membership) axis: SOPs apply to *roles*, *seats*, and
 * *departments* only.
 *
 * The full selected set is the single source of truth (`value` / `onChange`).
 * AI suggestions render as toggle chips that add/remove a matching target;
 * anything not currently matched by a visible suggestion renders as a
 * removable chip (with an "AI" marker when it originated from a suggestion).
 */

const TARGET_TYPE_LABELS: Record<SopTargetType, string> = {
  all: 'Everyone in the org',
  role: 'Role',
  seat: 'Seat',
  department: 'Department',
};

function formatRole(role: string): string {
  return role
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

/** A stable key for a target so it can be de-duped + toggled. */
function targetKey(t: {
  targetType: SopTargetType;
  targetRole?: string | null;
  targetSeatId?: string | null;
  targetDepartmentId?: string | null;
}): string {
  switch (t.targetType) {
    case 'all':
      return 'all';
    case 'role':
      return `role:${t.targetRole ?? ''}`;
    case 'seat':
      return `seat:${t.targetSeatId ?? ''}`;
    case 'department':
      return `department:${t.targetDepartmentId ?? ''}`;
  }
}

function suggestionToInput(s: ApplicabilitySuggestion): SopApplicabilityInput {
  return {
    targetType: s.targetType,
    targetRole: s.targetType === 'role' ? s.targetRole : null,
    targetSeatId: s.targetType === 'seat' ? s.targetSeatId : null,
    targetDepartmentId: s.targetType === 'department' ? s.targetDepartmentId : null,
    aiSuggested: true,
  };
}

function describeTarget(a: SopApplicabilityInput, seats: SeatView[], departments: Department[]): string {
  switch (a.targetType) {
    case 'all':
      return 'Everyone in the organization';
    case 'role':
      return `Role: ${formatRole(a.targetRole ?? '')}`;
    case 'seat':
      return `Seat: ${seats.find((s) => s.id === a.targetSeatId)?.name ?? a.targetSeatId ?? ''}`;
    case 'department':
      return `Dept: ${departments.find((d) => d.id === a.targetDepartmentId)?.name ?? a.targetDepartmentId ?? ''}`;
  }
}

export interface ApplicabilityEditorProps {
  value: SopApplicabilityInput[];
  onChange: (next: SopApplicabilityInput[]) => void;
  suggestions?: ApplicabilitySuggestion[];
  seats: SeatView[];
  departments: Department[];
  /** Prefix so multiple editors on one page keep unique field ids (a11y). */
  idPrefix?: string;
}

export function ApplicabilityEditor({
  value,
  onChange,
  suggestions = [],
  seats,
  departments,
  idPrefix = 'applies',
}: ApplicabilityEditorProps) {
  const [targetType, setTargetType] = useState<SopTargetType>('role');
  const [role, setRole] = useState<SystemRole>('employee');
  const [seatId, setSeatId] = useState('');
  const [departmentId, setDepartmentId] = useState('');

  const selectedKeys = new Set(value.map((v) => targetKey(v)));
  const suggestionKeys = new Set(suggestions.map((s) => targetKey(s)));

  function addTarget(next: SopApplicabilityInput): void {
    if (selectedKeys.has(targetKey(next))) return; // de-dupe
    onChange([...value, next]);
  }

  function removeKey(key: string): void {
    onChange(value.filter((v) => targetKey(v) !== key));
  }

  function toggleSuggestion(s: ApplicabilitySuggestion): void {
    const key = targetKey(s);
    if (selectedKeys.has(key)) removeKey(key);
    else onChange([...value, suggestionToInput(s)]);
  }

  function addManual(): void {
    if (targetType === 'all') {
      addTarget({ targetType: 'all', aiSuggested: false });
    } else if (targetType === 'role') {
      addTarget({ targetType: 'role', targetRole: role, aiSuggested: false });
    } else if (targetType === 'seat') {
      if (!seatId) return;
      addTarget({ targetType: 'seat', targetSeatId: seatId, aiSuggested: false });
    } else {
      if (!departmentId) return;
      addTarget({ targetType: 'department', targetDepartmentId: departmentId, aiSuggested: false });
    }
  }

  // Chips: everything selected that isn't currently shown as a suggestion
  // toggle (so accepted suggestions live in the suggestions strip, while
  // manual + previously-saved targets show here).
  const chips = value.filter((v) => !suggestionKeys.has(targetKey(v)));

  return (
    <div className="space-y-3">
      {/* AI suggestions */}
      {suggestions.length > 0 ? (
        <div className="space-y-2">
          <p className="flex items-center gap-1.5 text-[12px] font-medium text-kw-muted">
            <Sparkles className="size-3.5" />
            Suggested applicability
          </p>
          <div className="space-y-1.5">
            {suggestions.map((s) => {
              const key = targetKey(s);
              const checked = selectedKeys.has(key);
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => {
                    toggleSuggestion(s);
                  }}
                  aria-pressed={checked}
                  className={
                    checked
                      ? 'flex w-full items-start gap-3 rounded-kw-md border border-kw-cerulean bg-kw-cerulean-light/60 p-2.5 text-left'
                      : 'flex w-full items-start gap-3 rounded-kw-md border border-kw-border bg-white p-2.5 text-left hover:border-kw-cerulean/40'
                  }
                >
                  <span
                    className={
                      checked
                        ? 'mt-0.5 grid size-4 shrink-0 place-items-center rounded border border-kw-cerulean bg-kw-cerulean text-white'
                        : 'mt-0.5 size-4 shrink-0 rounded border border-kw-border'
                    }
                  >
                    {checked ? <Check className="size-3" /> : null}
                  </span>
                  <span className="min-w-0">
                    <span className="block text-[13px] font-medium text-kw-shark">{s.targetLabel}</span>
                    <span className="block text-[12px] text-kw-muted">{s.reason}</span>
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      ) : null}

      {/* Manual add row */}
      <div className="flex flex-wrap items-end gap-2 rounded-kw-md border border-kw-border bg-kw-bg p-3">
        <SelectField
          idPrefix={idPrefix}
          label="Applies to"
          value={targetType}
          onChange={(v) => {
            setTargetType(v as SopTargetType);
          }}
          options={(Object.keys(TARGET_TYPE_LABELS) as SopTargetType[]).map((t) => ({
            value: t,
            label: TARGET_TYPE_LABELS[t],
          }))}
        />
        {targetType === 'role' ? (
          <SelectField
            idPrefix={idPrefix}
            label="Role"
            value={role}
            onChange={(v) => {
              setRole(v as SystemRole);
            }}
            options={SYSTEM_ROLES.map((r) => ({ value: r, label: formatRole(r) }))}
          />
        ) : null}
        {targetType === 'seat' ? (
          <SelectField
            idPrefix={idPrefix}
            label="Seat"
            value={seatId}
            onChange={setSeatId}
            options={[
              { value: '', label: 'Select a seat…' },
              ...seats.map((s) => ({ value: s.id, label: s.name })),
            ]}
          />
        ) : null}
        {targetType === 'department' ? (
          <SelectField
            idPrefix={idPrefix}
            label="Department"
            value={departmentId}
            onChange={setDepartmentId}
            options={[
              { value: '', label: 'Select a department…' },
              ...departments.map((d) => ({ value: d.id, label: d.name })),
            ]}
          />
        ) : null}
        <Button type="button" size="sm" variant="secondary" onClick={addManual}>
          <Plus className="size-4" />
          Add
        </Button>
      </div>

      {/* Selected chips */}
      {chips.length > 0 ? (
        <ul className="flex flex-wrap gap-2">
          {chips.map((a) => {
            const key = targetKey(a);
            return (
              <li
                key={key}
                className="inline-flex items-center gap-1.5 rounded-kw-full bg-kw-cerulean-light px-2.5 py-1 text-[12px] text-kw-cerulean-dark"
              >
                {describeTarget(a, seats, departments)}
                {a.aiSuggested ? (
                  <span className="rounded-kw-full bg-kw-cerulean px-1.5 text-[9px] font-semibold uppercase tracking-wide text-white">
                    AI
                  </span>
                ) : null}
                <button
                  type="button"
                  aria-label="Remove applicability"
                  onClick={() => {
                    removeKey(key);
                  }}
                  className="text-kw-cerulean-dark/70 hover:text-kw-danger-text"
                >
                  <X className="size-3" />
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
}

function SelectField({
  idPrefix,
  label,
  value,
  onChange,
  options,
}: {
  idPrefix: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
}) {
  const id = `${idPrefix}-${label.toLowerCase().replace(/\s+/g, '-')}`;
  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={id} className="text-[11px] font-medium text-kw-muted">
        {label}
      </label>
      <select
        id={id}
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
        }}
        className="rounded-kw-md border border-kw-border bg-white px-2.5 py-1.5 text-[13px] text-kw-shark focus:border-kw-cerulean focus:outline-none focus:ring-2 focus:ring-kw-cerulean/20"
      >
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    </div>
  );
}
